import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The smallest thing that replaces `useEffect(() => { fetch().then(setState) })`.
 *
 * Every page in this panel wrote that pattern by hand, which meant none of
 * them had an error state — a failed request left the page showing "No users
 * found" as though the table were empty. It also meant a slow request that
 * resolved after the user had navigated on would set state on a dead
 * component, and two requests in flight could resolve out of order and leave
 * stale data on screen.
 *
 * Not react-query. This panel needs cancellation and an error channel, not a
 * cache layer, and adding a dependency the week of launch is a bad trade.
 */

export type QueryState<T> = {
  data: T | null;
  error: string | null;
  /** True on the very first load only — use it to choose skeleton vs spinner. */
  loading: boolean;
  /** True on every load including refetches. */
  fetching: boolean;
  refetch: () => void;
};

export function useQuery<T>(
  fn: () => Promise<T>,
  deps: unknown[]
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(true);

  // Monotonic request id. Only the newest request is allowed to write state,
  // so a slow first request can't overwrite a fast second one.
  const latest = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    const id = ++latest.current;
    setFetching(true);
    setError(null);
    try {
      const result = await fn();
      if (!alive.current || id !== latest.current) return;
      setData(result);
    } catch (err: any) {
      if (!alive.current || id !== latest.current) return;
      setError(err?.message ?? "Something went wrong");
    } finally {
      if (alive.current && id === latest.current) {
        setLoading(false);
        setFetching(false);
      }
    }
    // `fn` is intentionally not a dependency — it's a fresh closure on every
    // render, and depending on it would loop. `deps` is the contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { data, error, loading, fetching, refetch: run };
}
